import { useState } from "react";
import { Dropdown, Menu, notification } from "antd";
import clsx from "clsx";
import { JournalsStatuses, MainJournal } from "@/entities/journal";
import { BasketIcon, MoreOneIcon, VectorIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useCoordinator } from "@/shared/lib/hooks";
import { routes, useNavigate } from "@/shared/lib/react-router";
import { NOTIFICATION_MESSAGES } from "@/shared/utils";
import { useDeleteJournal } from "../../model/delete-journal.mutation";
import { ObjectJournalsSkeleton } from "../object-journals-skeleton";
import { JournalSectionSummary } from "./ui/journal-section-summary";
import "./object-journals-element.scss";

interface IObjectJournalsElement {
  journalInfo: MainJournal;
  address: string;
  isV2?: boolean;
  defaultOpen?: boolean;
}

export const ObjectJournalsElement = ({
  journalInfo,
  address,
  isV2,
  defaultOpen = false,
}: IObjectJournalsElement) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isClosing, setIsClosing] = useState(false);
  const navigate = useNavigate();
  const { projectId, objectId } = useCoordinator() as {
    projectId: string;
    objectId: string;
    titleId: string;
  };

  const { mutate, isPending } = useDeleteJournal({
    buildingObjectId: objectId,
    page: 1,
    limit: 10,
  });

  const handleJournalClick = () => {
    navigate(
      routes.journals.title(
        projectId,
        objectId,
        journalInfo.mainJournalId,
        journalInfo.titleJournal.titleId,
      ),
    );
  };

  const handleDeleteJournal = () => {
    mutate(
      { mainJournalId: journalInfo.mainJournalId },
      {
        onSuccess: () => {
          notification.success({
            message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
            description: NOTIFICATION_MESSAGES.JOURNAL_DELETED_DESC, // "Удалили журнал"
            placement: "topRight",
            showProgress: false,
          });
        },
        onError: () => {
          notification.error({
            message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
            description: NOTIFICATION_MESSAGES.JOURNAL_DELETE_FAILED_DESC, // "Невозможно удалить"
            placement: "topRight",
            showProgress: false,
          });
        },
      },
    );
  };

  const handleOpen = () => {
    if (isOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
      }, 300);
    } else {
      setIsOpen(true);
    }
  };

  const menu = (
    <Menu>
      {/* <Menu.Item key="copy" icon={<CopyIcon />} onClick={handleCopy}>
        Скопировать
      </Menu.Item> */}
      <Menu.Item
        key="delete"
        icon={<BasketIcon />}
        onClick={handleDeleteJournal}
      >
        Удалить
      </Menu.Item>
    </Menu>
  );

  if (isPending) {
    return <ObjectJournalsSkeleton />;
  }

  return (
    <div
      className={clsx("table-container", "journal-container", isV2 && "journal-container--collapsible")}
    >
      <div
        className="journal-header-container"
        onClick={isV2 ? handleOpen : undefined}
      >
        <div className="journal-info-container">
          {isV2 && (
            <div className={clsx("journal-arrow-icon", isOpen && "rotated")}>
              <VectorIcon />
            </div>
          )}

          <span
            className="journal-title-link"
            onClick={(e) => {
              e.stopPropagation();
              handleJournalClick();
            }}
          >
            {"Общий журнал работ "}
            {journalInfo.titleJournal.mainJournalNumber
              ? `№${journalInfo.titleJournal.mainJournalNumber}`
              : ""}
          </span>
          <div className="journal-tags-container">
            <div className={clsx("journal-tag", journalInfo.status.toLowerCase())}>
              {JournalsStatuses[journalInfo.status]}
            </div>
          </div>
        </div>

        <div className="journal-date-container">
          <div className="journal-date-info">
            {new Date(journalInfo.createDate).toLocaleDateString("ru-RU", {
              timeZone: "Europe/Moscow",
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })}
          </div>
          <div className="journal-date-time">
            {new Date(journalInfo.createDate).toLocaleTimeString("ru-RU", {
              timeZone: "Europe/Moscow",
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            МСК
          </div>
          <Can I="edit" a="object">
            <Dropdown overlay={menu} trigger={["click"]}>
              <div>
                <MoreOneIcon
                  style={{ cursor: "pointer" }}
                  width={24}
                  height={24}
                />
              </div>
            </Dropdown>
          </Can>
        </div>
      </div>

      <div className="journal-description">{address}</div>

      {isV2 && (isOpen || isClosing) && (
        <JournalSectionSummary
          journalInfo={journalInfo}
          isClosing={isClosing}
        />
      )}
    </div>
  );
};
